import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostBinding,
  HostListener,
  Injector,
  Input,
  Output,
  ViewChild,
} from '@angular/core';
import { UntypedFormControl, ValidatorFn, Validators } from '@angular/forms';
import { delayedPromise, replaceNbspFromHtml, trimEmptyHtmlRows } from '@anecdotes/utils';
import { AbstractValueAccessor, MakeProvider } from '../abstract-value-accessor';
import { CustomValidators } from '@anecdotes/validators';

const MULTILINE_MIN_HEIGHT = 68;

@Component({
  selector: 'app-editable-field',
  templateUrl: './editable-field.component.html',
  styleUrls: ['./editable-field.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [MakeProvider(EditableFieldComponent)],
})
export class EditableFieldComponent extends AbstractValueAccessor {
  private _text: string;

  @ViewChild('textarea')
  private textareaComponent: ElementRef<HTMLTextAreaElement>;

  @HostBinding('class')
  private classes = 'flex flex-col font-main w-full overflow-visible';

  @Input()
  rowsCount = 999;

  @Input()
  multilineDisplayText = false;

  @Input()
  isFormatted = false;

  get fieldText(): string {
    return this._text;
  }
  @Input()
  set fieldText(text: string) {
    this._text = text;
    this.textSetCallback();
  }

  textToDisplay: string;

  @Input()
  placeholder: string;

  @Input()
  label: string;

  @Input()
  labelInfo: string;

  @Input()
  submitOnEnter = true;

  @Input()
  isRequired: boolean;

  @Input()
  noWhiteSpace: boolean;

  @Input()
  maxLength: number;

  @Input()
  displayCharactersCounter = false;

  @Input()
  displayLineBreaks = false;

  @Input()
  isOneLine = false;

  @Input()
  customValidator: (value: string) => boolean;

  @Input()
  customValidatorErrorMessage: string;

  @Input()
  shouldDisableEditByClick = false;

  @Input()
  isTextBold = false;

  @Input()
  font = 'font-main';

  @Input()
  textSizeClass = 'text-xl';

  @Input()
  textColorClass = 'text-navy-70';

  @Input()
  confirmationButtonsPosition: 'absolute-left-bottom' | 'right' = 'absolute-left-bottom';

  @Input()
  shouldDisplayTooltip = true;

  @Input()
  preserveWhitespace: boolean;

  @Input()
  hoverable = true;

  @Input()
  hasPencilIcon = false;

  @Output()
  // eslint-disable-next-line @angular-eslint/no-output-native
  submit = new EventEmitter<string>();

  fieldControl: UntypedFormControl;

  get editableFieldWrapperClasses(): string {
    if (this.confirmationButtonsPosition === 'right') {
      return 'gap-4 flex-row items-center';
    }

    if (this.confirmationButtonsPosition === 'absolute-left-bottom') {
      return 'flex-col';
    }
  }

  get editConfirmationButtonsWrapperClasses(): string {
    if (this.confirmationButtonsPosition === 'right') {
      return '';
    }

    if (this.confirmationButtonsPosition === 'absolute-left-bottom') {
      return 'm-1';
    }
  }

  get editConfirmationButtonsClasses(): string {
    if (this.confirmationButtonsPosition === 'right') {
      return 'left-0 relative';
    }

    if (this.confirmationButtonsPosition === 'absolute-left-bottom') {
      return 'left-full absolute';
    }
  }

  get hasChanges(): boolean {
    let value = this.fieldControl?.value?.trim();

    if (value && this.isFormatted) {
      value = trimEmptyHtmlRows(value);
    }

    const fieldText = this.fieldText?.trim();

    if (fieldText) {
      return value !== fieldText;
    }

    return !!value;
  }

  get isEditMode(): boolean {
    return !!this.fieldControl;
  }

  get isSaveButtonDisabled(): boolean {
    return !this.hasChanges || this.fieldControl.invalid || this.isDisabled || !this.customValidatorCheck(this.fieldControl.value);
  }

  get isPencilIconDisplayed(): boolean {
    return this.fieldText && this.hasPencilIcon && !this.isDisabled;
  }

  get isClickable(): boolean {
    return !this.shouldDisableEditByClick && !this.isDisabled;
  }

  constructor(
    private readonly cd: ChangeDetectorRef,
    private readonly hostElementRef: ElementRef<HTMLElement>,
    injector: Injector,
  ) {
    super(injector);
  }

  override writeValue(value: string | undefined): void {
    this.value = value;
    this.fieldText = value;
  }

  async enableEditableMode(event: MouseEvent): Promise<void> {
    event.stopPropagation();

    if (this.isFormatted) {
      const tagName = (event.target as HTMLElement).tagName;
      const isAnchor = tagName.toLowerCase() === 'a';

      if (isAnchor) {
        return;
      }
    }

    const value = this.formControl ? this.value : this.textToDisplay;
    this.fieldControl = new UntypedFormControl(value, this.formValidators);
    this.cd.detectChanges();

    if (!this.isFormatted) {
      this.setTextAreaHeight();
      await delayedPromise(100);
      this.textareaComponent.nativeElement.focus();
    }
  }

  submitEditing(event: Event): void {
    event.preventDefault();

    if (!this.hasChanges || !this.fieldControl.valid) {
      this.cancelEditing();
      return;
    }

    let value = this.fieldControl.value || '';
    const hasWhiteSpace = value.trim() === '' && value !== value.trim();

    const isInvalid = (hasWhiteSpace && this.noWhiteSpace) || this.fieldControl.invalid;
    const isIncorrectSubmit = !this.submitOnEnter && (event as KeyboardEvent)?.key === 'Enter';

    if (isIncorrectSubmit || isInvalid || !this.customValidatorCheck(value)) {
      return;
    }

    value = value.trim();

    if (this.isFormatted) {
      value = trimEmptyHtmlRows(value);
      value = replaceNbspFromHtml(value);
    }

    this.textToDisplay = value;

    if (this.formControl) {
      this.writeValue(value);
    }

    this.submit.emit(value);
    this.cancelEditing();
  }

  preventNewLine(event: Event): void {
    if (this.submitOnEnter || this.isOneLine) {
      event.preventDefault();
    }
  }

  textChange(event: Event): void {
    event.stopPropagation();

    this.setTextAreaHeight();
  }

  cancelEditing(): void {
    this.fieldControl = null;
    this.cd.detectChanges();
  }

  customValidatorCheck(value: string): boolean {
    if (this.customValidator) {
      return this.customValidator(value);
    }

    return true;
  }

  private textSetCallback(): void {
    this.textToDisplay = this._text;
    setTimeout(() => this.cd.detectChanges(), 500); // TODO
  }

  private setTextAreaHeight(): void {
    if (this.textareaComponent.nativeElement.scrollHeight > MULTILINE_MIN_HEIGHT) {
      this.textareaComponent.nativeElement.style.height = `${this.textareaComponent.nativeElement.scrollHeight}px`;
    }
  }

  @HostListener('window:mousedown', ['$event'])
  private windowClick(mouseEvent: MouseEvent): void {
    // Handles outside clicks to cancel editing when there are no changes
    if (
      this.fieldControl &&
      !this.hasChanges &&
      !mouseEvent.composedPath().some((target) => target === this.hostElementRef.nativeElement)
    ) {
      this.cancelEditing();
    }
  }

  private get formValidators(): ValidatorFn[] | null {
    if (this.formControl) {
      return [this.formControl.validator];
    }

    let validators: ValidatorFn[] | null = null;

    if (this.isRequired) {
      validators = [Validators.required, CustomValidators.noWhiteSpaceOnly];
    }

    if (this.noWhiteSpace) {
      if (validators) {
        validators.push(CustomValidators.noWhiteSpaceOnly);
      } else {
        validators = [CustomValidators.noWhiteSpaceOnly];
      }
    }

    if (this.maxLength && !this.isFormatted) {
      if (validators) {
        validators.push(Validators.maxLength(this.maxLength));
      } else {
        validators = [Validators.maxLength(this.maxLength)];
      }
    }

    return validators;
  }
}
